import { inject } from '@angular/core';
import { isDefined } from '@hyundai/shared-lib';
import { Store } from '@ngrx/store';
import { filter, firstValueFrom } from 'rxjs';
import { DealerFeature, VehicleFeature } from '../+state';
import { state } from '../+state/dealer/dealer.reducer';
import type { Dealer } from '../api/api-types';
import { isHWS } from '../providers/platform.token';
import { selectQueryParam } from './router-selectors';

export function titleResolver(title: (dealer: Dealer, store: Store) => Promise<string> | string) {
    return async () => {
        const store = inject(Store);
        const dealer = await firstValueFrom(store.select(state.selectLoadedDealer).pipe(filter(isDefined)));
        return `${await title(dealer, store)}`;
    };
}

export function extraDealerTitleResolver(title: (store: Store) => Promise<string> | string) {
    return titleResolver(async (dealer, store) => `${await title(store)} | ${dealer.name} ${dealer.city}`);
}
export function defaultTitleResolver(title: string | ((store: Store) => string)) {
    return titleResolver((dealer) => `${title} | ${dealer.name} in ${dealer.city}`);
}

export function createTitleResolverVehicleMarket(title: (store: Store, isHWS: boolean) => Promise<string> | string) {
    return async () => {
        return `${await title(inject(Store), isHWS())}`;
    };
}

export function createTitleResolverSearchabo(title: (store: Store, isHWS: boolean) => Promise<string> | string) {
    return async () => {
        return `${await title(inject(Store), isHWS())}`;
    };
}

export async function buildSearchaboTitle(title: string, store: Store, isHWS: boolean) {
    if (isHWS) {
        const dealer = await firstValueFrom(
            store.select(DealerFeature.state.selectLoadedDealer).pipe(filter(isDefined)),
        );
        return `${title} | ${dealer.name}`;
    }
    return `${title} | Hyundai Fahrzeugmarkt`;
}

export async function buildTitleByFilter(store: Store, isHWS: boolean) {
    if (isHWS) {
        const dealer = await firstValueFrom(
            store.select(DealerFeature.state.selectLoadedDealer).pipe(filter(isDefined)),
        );
        return `Unser Fahrzeugbestand | ${dealer.name} in ${dealer.city}`;
    }

    let title = 'Hyundai';
    const numberOfVehicles = await firstValueFrom(
        store.select(VehicleFeature.vehicleState.selectLoadedNumberOfVehicles),
    );
    if (numberOfVehicles && numberOfVehicles > 0 && !isHWS) {
        const filterQueryParam = await firstValueFrom(store.select(selectQueryParam('filter')));
        if (filterQueryParam && typeof filterQueryParam === 'string') {
            const { condition, model, fuel, category, hybridPlugin } = JSON.parse(filterQueryParam);
            const modelFilter = Array.isArray(model) && model.length === 1 ? model[0] : undefined;
            const fuelFilter = buildFuelString(hybridPlugin, fuel);
            const categoryFilter =
                Array.isArray(category) && category.length === 1 ? buildSeoCategoryFilter(category[0]) : undefined;
            const conditionFilter = buildSeoConditionFilter(condition);
            const filterLength = [modelFilter, fuelFilter, categoryFilter, conditionFilter].filter((f) =>
                Boolean(f),
            ).length;
            const filterLengthValid = filterLength > 0 && filterLength < 3;
            title = filterSwitch(filterLengthValid, modelFilter, conditionFilter, title, fuelFilter, categoryFilter);
        } else {
            return `Hyundai Fahrzeugbestand`;
        }
    }
    return `${title} | Hyundai Fahrzeugmarkt`;
}

function filterSwitch(
    filterLengthValid: boolean,
    modelFilter: string | undefined,
    conditionFilter: string | undefined,
    title: string,
    fuelFilter: string | undefined,
    categoryFilter: string | undefined,
) {
    if (filterLengthValid) {
        if (modelFilter && conditionFilter) {
            title += ` ${modelFilter} als ${conditionFilter}`;
        } else if (modelFilter && fuelFilter) {
            const fuelFilterString = buildSeoFuelFilter(fuelFilter);
            const showFuelFilter = !(
                fuelFilterString && modelFilter.toLowerCase().includes(fuelFilterString.toLowerCase())
            );
            title += ` ${modelFilter} ${showFuelFilter ? `${buildSeoFuelFilter(fuelFilter)} Modelle` : ''}`;
        } else if (modelFilter && categoryFilter) {
            title += ` ${modelFilter} ${categoryFilter} Modelle`;
        } else if (modelFilter) {
            title += ` ${modelFilter} `;
        } else if (categoryFilter && fuelFilter) {
            title += ` ${buildSeoFuelFilter(fuelFilter)} ${categoryFilter} Modelle`;
        } else if (categoryFilter && conditionFilter) {
            title += ` ${categoryFilter} Modelle als ${conditionFilter}  `;
        } else if (categoryFilter) {
            title += ` ${categoryFilter} Modelle`;
        } else if (fuelFilter && conditionFilter) {
            title += ` ${buildSeoFuelFilter(fuelFilter)} Modelle als ${conditionFilter}`;
        } else if (fuelFilter) {
            title += ` ${buildSeoFuelFilter(fuelFilter)} Modelle`;
        } else if (conditionFilter) {
            title += ` ${conditionFilter}`;
        }
    } else if (modelFilter) {
        title += ` ${modelFilter}`;
    } else {
        title = 'Hyundai Neu- und Gebrauchtwagen';
    }
    return title;
}

export function buildFuelString(hybridPlugin: boolean, fuel: string | string[] | undefined): string | undefined {
    if (hybridPlugin && !fuel) {
        return 'Plug-in-Hybrid';
    }
    if (Array.isArray(fuel) && fuel.length === 1) {
        return fuel[0];
    }
    return;
}

export function buildSeoConditionFilter(condition: string | string[] | undefined): string | undefined {
    let cond: string | undefined;
    if (Array.isArray(condition) && condition.length === 1) {
        cond = condition[0];
    } else if (typeof condition === 'string') {
        cond = condition;
    }
    const conditionMap = new Map<string, string>([
        ['Neufahrzeug', 'Neuwagen'],
        ['Gebrauchtfahrzeug', 'Gebrauchtwagen'],
    ]);
    return cond ? conditionMap.get(cond) : undefined;
}

export function buildSeoCategoryFilter(category: string | undefined): string | undefined {
    const categoryMap = new Map<string, string>([
        ['SUV/Geländewagen/Pickup', 'SUV'],
        ['Van/Kleinbus', 'Van'],
        ['Kombi', 'Kombi'],
        ['Limousine', 'Limousinen'],
        ['Kleinwagen', 'Kleinwagen'],
        ['Sportwagen/Coupé', 'Sportwagen'],
    ]);
    return categoryMap.get(category ?? '');
}

export function buildSeoFuelFilter(fuel: string | undefined): string | undefined {
    const fuelMap = new Map<string, string>([
        ['Benzin', 'Benzin'],
        ['Diesel', 'Diesel'],
        ['Hybrid', 'Hybrid'],
        ['Hybrid (Benzin/Elektro)', 'Hybrid'],
        ['Plug-in-Hybrid', 'Plug-in-Hybrid '],
        ['Elektro', 'Elektro'],
    ]);
    return fuel ? fuelMap.get(fuel) : undefined;
}
